import React, { useState, useEffect } from 'react';
import { db, doc, getDoc, setDoc, serverTimestamp } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { FiSave, FiSettings, FiShield } from 'react-icons/fi';
import { toast } from 'react-toastify';

const AdminSettingsPage = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    deliveryFee: 30,
    freeDeliveryThreshold: 100,
    cookieConsent: {
      enabled: true,
      analyticsEnabled: true,
      marketingEnabled: true,
      bannerText: 'We use cookies to improve your experience on our site. By continuing to use our website, you agree to our use of cookies.',
      acceptButtonText: 'Accept All',
      declineButtonText: 'Decline',
      settingsButtonText: 'Cookie Settings',
      privacyPolicyUrl: '/privacy-policy'
    }
  });

  // Fetch current settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Only allow admin
        if (!currentUser || !currentUser.isAdmin) {
          setLoading(false);
          return;
        }

        const settingsRef = doc(db, 'settings', 'global');
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          setSettings({
            deliveryFee: data.deliveryFee || 30,
            freeDeliveryThreshold: data.freeDeliveryThreshold || 100,
            cookieConsent: {
              enabled: data.cookieConsent?.enabled ?? true,
              analyticsEnabled: data.cookieConsent?.analyticsEnabled ?? true,
              marketingEnabled: data.cookieConsent?.marketingEnabled ?? true,
              bannerText: data.cookieConsent?.bannerText || 'We use cookies to improve your experience on our site. By continuing to use our website, you agree to our use of cookies.',
              acceptButtonText: data.cookieConsent?.acceptButtonText || 'Accept All',
              declineButtonText: data.cookieConsent?.declineButtonText || 'Decline',
              settingsButtonText: data.cookieConsent?.settingsButtonText || 'Cookie Settings',
              privacyPolicyUrl: data.cookieConsent?.privacyPolicyUrl || '/privacy-policy'
            }
          });
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('cookieConsent.')) {
      const cookieField = name.split('.')[1];
      setSettings({
        ...settings,
        cookieConsent: {
          ...settings.cookieConsent,
          [cookieField]: type === 'checkbox' ? checked : value
        }
      });
    } else {
      setSettings({
        ...settings,
        [name]: type === 'number' ? parseFloat(value) : value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !currentUser.isAdmin) {
      toast.error('You do not have permission to update settings');
      return;
    }
    
    setSaving(true);
    
    try {
      await setDoc(doc(db, 'settings', 'global'), {
        ...settings,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      }, { merge: true });
      
      toast.success('Settings updated successfully');
    } catch (err) {
      console.error('Error updating settings:', err);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Loading settings...</div>;
  }

  if (!currentUser || !currentUser.isAdmin) {
    return <div style={{ color: 'red', padding: 32, textAlign: 'center' }}>You do not have permission to view this page.</div>;
  }

  return (
    <div style={{ maxWidth: '800px', margin: '20px auto', padding: 24, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
        <FiSettings size={24} style={{ marginRight: 12, color: '#555' }} />
        <h2 style={{ margin: 0, color: '#333' }}>Global Settings</h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 18, color: '#444', marginBottom: 16 }}>Delivery Settings</h3>
          
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#555' }}>
              Delivery Fee (QAR)
            </label>
            <input
              type="number"
              name="deliveryFee"
              value={settings.deliveryFee}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: 8,
                fontSize: 16
              }}
              required
            />
            <p style={{ margin: '8px 0 0', color: '#666', fontSize: 14 }}>
              This is the standard delivery fee that will be applied to all orders.
            </p>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#555' }}>
              Free Delivery Threshold (QAR)
            </label>
            <input
              type="number"
              name="freeDeliveryThreshold"
              value={settings.freeDeliveryThreshold}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: 8,
                fontSize: 16
              }}
              required
            />
            <p style={{ margin: '8px 0 0', color: '#666', fontSize: 14 }}>
              Orders with a subtotal above this amount will qualify for free delivery.
            </p>
          </div>
        </div>

        <div style={{ marginBottom: 24, borderTop: '1px solid #eee', paddingTop: 24 }}>
          <h3 style={{ fontSize: 18, color: '#444', marginBottom: 16, display: 'flex', alignItems: 'center' }}>
            <FiShield size={18} style={{ marginRight: 8, color: '#555' }} />
            Cookie Consent Settings
          </h3>
          
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', marginBottom: 8, fontWeight: 600, color: '#555' }}>
              <input
                type="checkbox"
                name="cookieConsent.enabled"
                checked={settings.cookieConsent.enabled}
                onChange={handleInputChange}
                style={{ marginRight: 8 }}
              />
              Enable Cookie Consent Banner
            </label>
            <p style={{ margin: '8px 0 0', color: '#666', fontSize: 14 }}>
              Show cookie consent banner to new visitors
            </p>
          </div>

          {settings.cookieConsent.enabled && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#555' }}>
                  Banner Text
                </label>
                <textarea
                  name="cookieConsent.bannerText"
                  value={settings.cookieConsent.bannerText}
                  onChange={handleInputChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    fontSize: 14,
                    resize: 'vertical'
                  }}
                  placeholder="Enter the text to display in the cookie consent banner"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#555' }}>
                    Accept Button Text
                  </label>
                  <input
                    type="text"
                    name="cookieConsent.acceptButtonText"
                    value={settings.cookieConsent.acceptButtonText}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      fontSize: 14
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#555' }}>
                    Decline Button Text
                  </label>
                  <input
                    type="text"
                    name="cookieConsent.declineButtonText"
                    value={settings.cookieConsent.declineButtonText}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      fontSize: 14
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#555' }}>
                    Settings Button Text
                  </label>
                  <input
                    type="text"
                    name="cookieConsent.settingsButtonText"
                    value={settings.cookieConsent.settingsButtonText}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      fontSize: 14
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#555' }}>
                    Privacy Policy URL
                  </label>
                  <input
                    type="text"
                    name="cookieConsent.privacyPolicyUrl"
                    value={settings.cookieConsent.privacyPolicyUrl}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      fontSize: 14
                    }}
                    placeholder="/privacy-policy"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: 8, fontWeight: 600, color: '#555' }}>
                    <input
                      type="checkbox"
                      name="cookieConsent.analyticsEnabled"
                      checked={settings.cookieConsent.analyticsEnabled}
                      onChange={handleInputChange}
                      style={{ marginRight: 8 }}
                    />
                    Enable Analytics Cookies by Default
                  </label>
                  <p style={{ margin: '8px 0 0', color: '#666', fontSize: 12 }}>
                    Allow analytics tracking by default
                  </p>
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: 8, fontWeight: 600, color: '#555' }}>
                    <input
                      type="checkbox"
                      name="cookieConsent.marketingEnabled"
                      checked={settings.cookieConsent.marketingEnabled}
                      onChange={handleInputChange}
                      style={{ marginRight: 8 }}
                    />
                    Enable Marketing Cookies by Default
                  </label>
                  <p style={{ margin: '8px 0 0', color: '#666', fontSize: 12 }}>
                    Allow marketing tracking by default
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        <button
          type="submit"
          disabled={saving}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px 24px',
            background: saving ? '#ccc' : '#27ae60',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          <FiSave size={18} style={{ marginRight: 8 }} />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
    </div>
  );
};

export default AdminSettingsPage; 